import { Alert, Card, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import Label from 'src/components/label';
import { formatTimeAsHourMin } from 'src/utils/date-utils';
import { RouteBuddySimulateResponse, SimClocks, STOP_LABELS, StopLabel, TripStop } from './types';

function formatSimTime(iso: string, timezone: string) {
  return DateTime.fromISO(iso).setZone(timezone).toFormat('ccc LLL d, h:mm a');
}

function ClocksGroup({ title, clocks }: { title: string; clocks: SimClocks }) {
  return (
    <Stack gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center">
        <Label>{`Break ${formatTimeAsHourMin(clocks.break)}`}</Label>
        <Label>{`Drive ${formatTimeAsHourMin(clocks.drive)}`}</Label>
        <Label>{`Shift ${formatTimeAsHourMin(clocks.shift)}`}</Label>
        <Label>{`Cycle ${formatTimeAsHourMin(clocks.cycle)}`}</Label>
      </Stack>
    </Stack>
  );
}

// within 15 minutes of the loaded plan still counts as on schedule
function planDelta(stop: TripStop | undefined, arrival: string) {
  if (!stop?.plannedArrival) {
    return null;
  }
  const deltaHours = DateTime.fromISO(arrival).diff(DateTime.fromISO(stop.plannedArrival), 'hours').hours;
  if (Math.abs(deltaHours) <= 0.25) {
    return { text: 'On schedule', background: '#36b746' };
  }
  if (deltaHours > 0) {
    return { text: `${formatTimeAsHourMin(deltaHours)} behind the plan`, background: '#ff9800' };
  }
  return { text: `${formatTimeAsHourMin(-deltaHours)} ahead of the plan`, background: '#36b746' };
}

export default function VerdictCards({
  simulation,
  stops,
}: {
  simulation: RouteBuddySimulateResponse;
  stops: TripStop[];
}) {
  const { timezone } = simulation;

  return (
    <Stack gap={2}>
      {simulation.stopResults.map((stopResult) => {
        const stop = stops[stopResult.stopIndex];
        const typeName = STOP_LABELS[stopResult.label as StopLabel] || stopResult.label;
        const delta = planDelta(stop, stopResult.arrival);
        return (
          <Card key={stopResult.stopIndex} sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Stop {stopResult.stopIndex + 1} · {typeName}
            </Typography>
            {stop?.location.address && (
              <Typography variant="body2" color="text.secondary">
                {stop.location.address}
              </Typography>
            )}
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              Arrives {formatSimTime(stopResult.arrival, timezone)}
            </Typography>
            {stop?.plannedArrival && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Planned {formatSimTime(stop.plannedArrival, timezone)}
              </Typography>
            )}
            {delta && (
              <Label sx={{ mt: 0.5, mr: 1, color: 'white', background: delta.background }}>
                {delta.text}
              </Label>
            )}
            {stopResult.canMakeAppointment !== null && stopResult.slackHours !== null && (
              <Label
                sx={{
                  mt: 0.5,
                  color: 'white',
                  background: stopResult.canMakeAppointment ? '#36b746' : '#ff5631',
                }}
              >
                {stopResult.canMakeAppointment
                  ? `${formatTimeAsHourMin(stopResult.slackHours)} before the appointment`
                  : `${formatTimeAsHourMin(-stopResult.slackHours)} late`}
              </Label>
            )}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Departs {formatSimTime(stopResult.departure, timezone)}
            </Typography>
          </Card>
        );
      })}

      <Card sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Totals
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {simulation.totalMiles.toFixed(0)} miles, {formatTimeAsHourMin(simulation.totalDriveHours)} of
          driving over {simulation.days.length} day{simulation.days.length === 1 ? '' : 's'}
        </Typography>
        <Stack gap={1.5}>
          <ClocksGroup title="At start" clocks={simulation.clocksAtStart} />
          {simulation.stopResults.length > 0 && (
            <ClocksGroup
              title="At the end"
              clocks={simulation.stopResults[simulation.stopResults.length - 1].clocksAfter}
            />
          )}
        </Stack>
      </Card>

      {simulation.usedCycleRestart && (
        <Alert severity="warning">
          The driver runs out of 70h cycle hours on this trip, the plan includes a 34h restart.
        </Alert>
      )}
      {simulation.warnings.map((warning, index) => (
        <Alert key={index} severity="warning">
          {warning}
        </Alert>
      ))}
      <Typography variant="caption" color="text.secondary">
        Planned on the 70h/8-day rule with the route as driven by Google Maps (not truck-legal routing).
        Treat this as guidance, not dispatch instructions.
      </Typography>
    </Stack>
  );
}
